/**
 * Cliente HTTP de personas (coaches) para `apps/speak`. Port mínimo del
 * `personasApi` del cliente viejo (`apps/client/src/pages/personasApi.ts`),
 * limitado a lo que el CoachPicker del V2 necesita: listar el catálogo,
 * persistir la voz del módulo y previsualizarla.
 *
 * NO se importa desde `apps/client` a propósito (Speak no debe acoplarse al
 * cliente viejo). Todas las requests usan `credentials: 'include'`.
 */

import { createTypedApiClient } from '@orborix/auth-client';
import type { ConvTone, SpanishVariant, TtsProviderId } from '@orborix/shared-types';

export type PersonaCategory = 'home' | 'english' | 'coach';

/** Género de la voz (metadata del provider, backend 0364). `null` = no verificado. */
export type PersonaGender = 'female' | 'male' | 'neutral';

export interface PersonaDto {
  id: string;
  slug: string;
  name: string;
  description: string;
  voiceId: string;
  /** Provider de la voz (`elevenlabs` | `gemini`), derivado server-side de `voiceId`. */
  ttsProvider: string;
  isPreset: boolean;
  /** Contexto recomendado — filtramos por `'english'`. */
  category: PersonaCategory | null;
  /** Acento legible ('US' | 'UK' | …). */
  accent: string | null;
  /** Género de la voz para la etiqueta "Mujer"/"Hombre" del picker. */
  gender: PersonaGender | null;
}

/**
 * TTS ACTIVO de un módulo (`GET /api/voices/active?module=…`), resuelto por el
 * routing del backend (lo mismo que usa la sesión de voz). El CoachPicker lista
 * sólo las personas cuya voz es de este provider.
 */
export interface ActiveTtsDto {
  module: string;
  provider: TtsProviderId;
  model: string;
  /** Voz default del módulo para el provider (Gemini); `null` con ElevenLabs. */
  defaultVoiceId: string | null;
  /** `false` = sin alignment por carácter (Gemini) → sin karaoke. */
  alignment: boolean;
}

export interface PersonaSettingsDto {
  globalPersonaId: string;
  moduleOverrides: Record<string, string>;
}

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  error?: string;
}

// Rutas absolutas (`/api/personas*`, fuera del namespace del módulo english) ⇒
// el cliente va sin `basePath`.
const client = createTypedApiClient({
  basePath: '',
  errorName: 'PersonasApiError',
  abortError: 'wrap',
});

export const PersonasApiError = client.ApiError;
export type PersonasApiError = InstanceType<typeof client.ApiError>;

/**
 * Dos particularidades propias que no vale la pena generalizar en el cliente
 * compartido: manda `content-type` TAMBIÉN en las lecturas (sin body), y
 * respeta el `success: false` sobre un HTTP 200 (el backend de personas lo usa).
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await client.request(path, {
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  await client.ensureOk(res);
  const json = (await res.json()) as ApiEnvelope<T>;
  if (json.success === false) {
    throw new PersonasApiError(json.error ?? 'unknown_error', res.status, json.error ?? 'unknown');
  }
  return json.data ?? (json as unknown as T);
}

export const personasApi = {
  /** Catálogo completo de personas. El caller filtra por `category`. */
  list(): Promise<PersonaDto[]> {
    return request<PersonaDto[]>('/api/personas');
  },
  /** Settings de personas del user — usamos `moduleOverrides.english` para preseleccionar. */
  settings(): Promise<PersonaSettingsDto> {
    return request<PersonaSettingsDto>('/api/personas/settings');
  },
  /** Persiste el override de voz del módulo (`user_persona_settings.module_overrides[moduleSlug]`). */
  setModule(moduleSlug: string, personaId: string | null): Promise<PersonaSettingsDto> {
    return request<PersonaSettingsDto>(
      `/api/personas/settings/module/${encodeURIComponent(moduleSlug)}`,
      { method: 'PUT', body: JSON.stringify({ personaId }) },
    );
  },
  /** Provider TTS activo del módulo — decide qué voces muestra el CoachPicker. */
  activeTts(moduleSlug: string): Promise<ActiveTtsDto> {
    return request<ActiveTtsDto>(`/api/voices/active?module=${encodeURIComponent(moduleSlug)}`);
  },
  /**
   * Audio de muestra de la voz, para el botón "Probar voz". El formato lo
   * decide el provider de la voz (ElevenLabs → audio/mpeg, Gemini → audio/wav):
   * el blob lleva el content-type del server, el `<audio>` reproduce ambos.
   * `tone` (Gemini): la demo suena con la identidad de voz de ese tono (demo
   * del selector de tono); ElevenLabs lo ignora.
   */
  async preview(
    personaId: string,
    text?: string,
    tone?: ConvTone,
    spanishVariant?: SpanishVariant,
  ): Promise<Blob> {
    // Antes dejaba escapar el `TypeError` crudo de `fetch` ante un fallo de red;
    // ahora rechaza con `PersonasApiError` como el resto. El único caller
    // (`audioBlobPlayer`) usa un `.catch()` genérico ⇒ nada cambia para el user.
    const res = await client.request(`/api/personas/${personaId}/preview`, {
      method: 'POST',
      body: JSON.stringify({
        ...(text ? { text } : {}),
        ...(tone ? { tone } : {}),
        // "Español del profe" (Gemini): suma el acento de las partes en español.
        ...(spanishVariant ? { spanishVariant } : {}),
      }),
    });
    await client.ensureOk(res);
    return res.blob();
  },
};
